import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import NumberInput from '~/components/Inputs/NumberInput';
import { DoseId } from '~/domain/changes/ids';
import { DoseDisplayView } from '~/hooks/dose/doseDisplayView';

export const DOSE_NORMALIZATION_STEP = 1;
export const DOSE_NORMALIZATION_MIN_VALUE = 1;
export const DOSE_NORMALIZATION_MAX_VALUE = 100;

type DoseNormalizationProps = {
    dose?: DoseDisplayView;
    /** Returns false when the store rejected the value (nothing applied). */
    setNormalizationValue: (value: number) => boolean;
};

const DoseNormalization = ({
    dose,
    setNormalizationValue,
}: DoseNormalizationProps) => {
    const doseId = dose?.id;
    const normalizationValueGy = dose?.normalizationValueGy ?? null;
    const [inputValue, setInputValue] = useState<number | null>(
        normalizationValueGy
    );
    const [shownDoseId, setShownDoseId] = useState<DoseId | undefined>(doseId);
    const intl = useIntl();
    const [error, setError] = useState<string | null>(null);

    // The selected dose can change while this input is mounted, with no
    // click (a completed prediction switches the selected dose). Re-seed
    // instead of showing the previous dose's normalization next to the new
    // dose's live values: the two rows would contradict each other with
    // nothing to say which is real. React's "adjust state during render"
    // pattern.
    if (doseId !== shownDoseId) {
        setShownDoseId(doseId);
        setInputValue(normalizationValueGy);
        setError(null);
    }

    const outOfRangeMessage = intl.formatMessage(
        { id: 'valueMustBeBetween' },
        {
            min: DOSE_NORMALIZATION_MIN_VALUE,
            max: DOSE_NORMALIZATION_MAX_VALUE,
        }
    );

    const handleInputChange = (newValue: number | null) => {
        if (newValue === null) {
            return;
        }
        setInputValue(newValue);
        // Out of range or non-finite: reject and say so. Never clamp a
        // clinical value into range behind the user's back — every
        // percent-of-prescription readout is referenced to this number.
        if (
            !Number.isFinite(newValue) ||
            newValue < DOSE_NORMALIZATION_MIN_VALUE ||
            newValue > DOSE_NORMALIZATION_MAX_VALUE
        ) {
            setError(outOfRangeMessage);
            return;
        }
        if (!setNormalizationValue(newValue)) {
            // The display store applies the same validity rule as
            // SR-WOR-H-4-1 and keeps the previous value on rejection. Show
            // the rejection rather than leaving the typed number looking
            // accepted while the dose still normalizes to the old one.
            setError(outOfRangeMessage);
            return;
        }
        setError(null);
    };

    return (
        <Box>
            <NumberInput
                value={inputValue}
                fullWidth
                label={<FormattedMessage id="normalizationValue" />}
                error={error}
                min={DOSE_NORMALIZATION_MIN_VALUE}
                max={DOSE_NORMALIZATION_MAX_VALUE}
                step={DOSE_NORMALIZATION_STEP}
                onChange={handleInputChange}
                disabled={dose?.provenance !== 'predicted'}
                data-testid="set-dose-normalization"
            />
            <Typography variant="body2" fontSize={11} mt={0.5}>
                <FormattedMessage id="doseNormalizationInfo" />
            </Typography>
        </Box>
    );
};

export default DoseNormalization;
